import { useEffect, useState } from "react";

import { getSubdomainBranding, type SubdomainBranding } from "@/utils/subdomainBranding";

/**
 * Reads the tenant branding the root route loader already fetched for this
 * subdomain. `getSubdomainBranding` caches the in-flight promise, so calling it
 * from a component costs no extra request -- it resolves against the same fetch
 * the loader made on page load.
 *
 * This exists so components below the root can read the branding without
 * reaching for the entity record, which entity users are not permitted to read.
 */
export function useSubdomainBranding(): SubdomainBranding | null {
  const [branding, setBranding] = useState<SubdomainBranding | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Read once on mount. resetSubdomainBrandingCache() (used by the branding
    // modal after a save) therefore does not reach this component until a full
    // reload -- acceptable, since only entity roles read branding here and they
    // cannot open that modal.
    getSubdomainBranding()
      .then((resolved) => {
        if (!cancelled) setBranding(resolved);
      })
      .catch(() => {
        if (!cancelled) setBranding(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return branding;
}
